import { useEffect, useMemo, useState } from "react";
import {
  getMondaySlugsDetails,
  getLastUsedAccountSlug,
  getMondayFullUrlForSlug
} from "services/monday-platform/existing-accounts-service";
import RegularButton from "segments/desktop/core-components/button/regular-button/regular-button";
import { LOGIN_PAGE_LINK, ROOT_LINK } from "constants/links";
import PictureComponent from "/components/core/picture/picture-component";
import { LG } from "components/core/new-button/button-component";
import { trackEvent } from "/services/bigbrain-service";
import {
  WELCOME_PAGE_DIDNT_FIND_ACCOUNT_CLICKED,
  WELCOME_PAGE_GO_TO_ACCOUNT_CLICKED,
  WELCOME_PAGE_LOADED_WITH_NO_ACCOUNTS,
  WELCOME_PAGE_LOADED
} from "constants/bigbrain-event-types";
import { WORK_OS_IRIS_COLOR_NAME } from "styles/color-consts";
import classnames from "classnames";

import { componentStyles } from "./welcome-back-homepage-first-fold-component.scss";

const MONDAY_LOGO = "https://dapulse-res.cloudinary.com/image/upload/welcome-back/Logo_monday.com.svg";
const HANDS_IMAGE_URL = "https://dapulse-res.cloudinary.com/image/upload/welcome-back/hands.png";

const FAKE_LAST_SLUG_DATA = {
  user_image: "https://cdn1.monday.com/dapulse_default_photo.png",
  user_name: "John Doe",
  user_email: "john.doe@gmail.com",
  account_name: "monday-demo",
  slug: "monday-demo",
  is_fake_slug: true
};

const WelcomeBackHomepageFirstFoldComponent = (props) => {
  const { translate, translateWithParam } = props;

  const [slugsDetails, setslugsDetails] = useState([]);
  const [lastSlug, setlastSlug] = useState(null);

  useEffect(() => {
    const slugsDetailsFromCookie = getMondaySlugsDetails();
    const lastSlugFromCookie = getLastUsedAccountSlug();

    if (slugsDetailsFromCookie.length === 0) {
      trackEvent(WELCOME_PAGE_LOADED_WITH_NO_ACCOUNTS, { info1: lastSlugFromCookie });
      window.location = ROOT_LINK;
    }
    trackEvent(WELCOME_PAGE_LOADED, { kind: slugsDetailsFromCookie.length, info1: lastSlugFromCookie });
    setslugsDetails(slugsDetailsFromCookie);
    setlastSlug(lastSlugFromCookie);
  }, []);

  const lastSlugDetails = useMemo(() => {
    const slug = slugsDetails.find((slugDetail) => slugDetail?.slug === lastSlug);
    return slug || FAKE_LAST_SLUG_DATA;
  }, [lastSlug, slugsDetails]);

  const olderSlugDetails = useMemo(() => {
    return slugsDetails.filter((slugDetail) => slugDetail?.slug !== lastSlug);
  }, [lastSlug, slugsDetails]);

  const isSlugLoaded = useMemo(() => {
    return !lastSlugDetails?.is_fake_slug;
  }, [lastSlugDetails]);

  const normalizeString = (str) => {
    return str?.replace(/\+/g, " "); // platform saves the name of the user with a + instead of space
  };

  const renderWelcomeText = () => {
    return (
      <div className="welcome-text-wrapper">
        <div className="welcome-text">{translate("welcomeBackComponent.welcomeBack")}</div>
        <div className="user-name">{normalizeString(lastSlugDetails?.user_name)} </div>
      </div>
    );
  };

  const renderFirstFoldMessage = () => {
    return (
      <>
        <div className="monday-logo">
          <PictureComponent className="monday-logo-picture" src={MONDAY_LOGO} />
        </div>
        <div className="welcome-text-and-image">
          {renderWelcomeText()}
          <div className="welcome-image">
            <PictureComponent className="hands-picture" src={HANDS_IMAGE_URL} />
          </div>
        </div>
      </>
    );
  };

  const renderLastAccountData = () => {
    const url = getMondayFullUrlForSlug(lastSlugDetails?.slug);
    return (
      <>
        <div className="last-account-title">{translateWithParam("welcomeBackComponent.lastAccountTitle")}</div>
        <div className="last-account-details">
          <div className="user-image-wrapper">
            <PictureComponent
              className="user-image"
              src={lastSlugDetails?.user_image}
              skipCloudinaryConversion={true}
            />
          </div>
          <div className="last-account-user-company">{normalizeString(lastSlugDetails?.account_name)}</div>
          <div className="last-account-user-email">{lastSlugDetails?.user_email}</div>
          <div className="last-account-button-wrapper">
            <RegularButton
              buttonText={translate("welcomeBackComponent.goToAccount")}
              url={url}
              isOpenLinkInNewTab={true}
              buttonSize={LG}
              onClick={() => onGoToAccountClicked(true, url)}
            />
          </div>
        </div>
      </>
    );
  };

  const renderAccountsData = () => {
    if (!lastSlugDetails) return null;

    return (
      <div className="accounts-details-box">
        <div className="last-account-details-wrapper">{renderLastAccountData()}</div>
        <div className="rest-accounts-wrapper">{renderOlderAccountBoxes()}</div>
      </div>
    );
  };

  const renderAccountBox = (slugDetails) => {
    const url = getMondayFullUrlForSlug(slugDetails?.slug);

    return (
      <div className="account-box">
        <div className="account-box-data">
          <div className="old-account-image-wrapper">
            <PictureComponent
              className="old-account-user-image"
              src={slugDetails?.user_image}
              skipCloudinaryConversion={true}
            />
          </div>
          <div className="old-account-user-details">
            <div className="old-account-user-company">{normalizeString(slugDetails?.account_name)}</div>
            <div className="old-account-user-email">{slugDetails?.user_email}</div>
          </div>
          <div className="old-account-button-wrapper">
            <RegularButton
              buttonText={translate("welcomeBackComponent.goToAccount")}
              url={url}
              isOpenLinkInNewTab={true}
              outline
              color={WORK_OS_IRIS_COLOR_NAME}
              onClick={() => onGoToAccountClicked(false, url)}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderOlderAccountBoxes = () => {
    if (olderSlugDetails.length === 0) return null;

    return (
      <div className="older-account-boxes-wrapper">
        <div className="older-account-title">{translate("welcomeBackComponent.restAccountTitle")}</div>
        {olderSlugDetails.map((slugDetails) => renderAccountBox(slugDetails))}
      </div>
    );
  };

  const onNoAccountClicked = () => {
    trackEvent(WELCOME_PAGE_DIDNT_FIND_ACCOUNT_CLICKED, { info1: slugsDetails.length });
  };

  const onGoToAccountClicked = (isLastAccount, url) => {
    trackEvent(WELCOME_PAGE_GO_TO_ACCOUNT_CLICKED, {
      kind: isLastAccount ? "last_account" : "old_account",
      info1: slugsDetails.length,
      info2: url
    });
  };

  const renderLoginOption = () => {
    return (
      <>
        <span className="no-account-text">{translate("welcomeBackComponent.notSeeingAccount")}</span>
        <a className="no-account-link" href={LOGIN_PAGE_LINK} onClick={onNoAccountClicked}>
          {translate("welcomeBackComponent.clickHere")}
        </a>
      </>
    );
  };

  return (
    <div
      className={classnames("welcome-back-homepage-first-fold-component", {
        "slugs-not-loaded": !isSlugLoaded
      })}
    >
      <div className="background-color-div" />
      <div className="welcome-back-first-fold-message">{renderFirstFoldMessage()}</div>
      <div className="accounts-wrapper">{renderAccountsData()}</div>
      <div className="general-login-wrapper">{renderLoginOption()}</div>

      <style jsx>{componentStyles}</style>
    </div>
  );
};

export default WelcomeBackHomepageFirstFoldComponent;
